import React, {useEffect, useMemo, useRef, useState} from 'react';
import {LessonPresentation} from '../../../../data/models/Course/LessonPresentation';
import {
  LessonContentAcknowledgment,
  LessonContentMChoice,
} from '../../../../data/models/LessonContent/LessonContentBase';
import {getMicroreelVideoSources} from '../microreelVideoSources';
import MicroreelVideoSurface from './MicroreelVideoSurface';
import {MicroreelLesson} from '../../../../data/models/Microreels/MicroreelGroup';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import {Icon} from '../../../../uiToolkit/Icon/Icon';
import {microreelsListText} from '../microreelsListText';
import {getAckTexts} from '../ackLocalization';
import styles from './MicroreelPhonePreview.module.css';

interface Props {
  groupTitle: string;
  reels: MicroreelLesson[];
  selectedReel?: MicroreelLesson;
  presentation?: LessonPresentation;
  isLoading: boolean;
  onSelectReel: (reel: MicroreelLesson) => void;
  onProgress: (fraction: number) => void;
  learnerMode?: boolean;
  question?: LessonContentMChoice;
  ackNode?: LessonContentAcknowledgment;
  // The generated "policy attestation" is a flag on the lesson (no content
  // node); when set, the preview mirrors the learner app's Yes/No screen.
  ackFlag?: boolean;
  // Language key chosen at generation time: the acknowledgment screen is
  // shown in the narration language.
  ackLanguage?: string;
  onExitPreview?: () => void;
}

const MicroreelPhonePreview = React.memo(
  ({
    groupTitle,
    reels,
    selectedReel,
    presentation,
    isLoading,
    onSelectReel,
    onProgress,
    learnerMode,
    question,
    ackNode,
    ackFlag,
    ackLanguage,
    onExitPreview,
  }: Props) => {
    const currentIndex = selectedReel
      ? reels.findIndex(reel => reel.id === selectedReel.id)
      : -1;

    const goToOffset = (offset: number) => {
      const next = reels[currentIndex + offset];
      if (next) {
        onSelectReel(next);
      }
    };

    const videoSources = useMemo(
      () => getMicroreelVideoSources(presentation?.lessonContent),
      [presentation],
    );

    const [fillFrame, setFillFrame] = useState(false);
    const [quizShown, setQuizShown] = useState(false);
    const [ackShown, setAckShown] = useState(false);
    const [pickedAnswer, setPickedAnswer] = useState<number | null>(null);
    const [submitted, setSubmitted] = useState(false);
    // The quiz and ack screens unmount the video, so the author's sound
    // choice is kept here for the next reel's player.
    const mutedPreferenceRef = useRef(true);

    useEffect(() => {
      setQuizShown(false);
      setAckShown(false);
      setPickedAnswer(null);
      setSubmitted(false);
    }, [selectedReel?.id]);

    // Learner mode: advance like the feed does — next reel, or leave the
    // preview after the last one.
    const advance = () => {
      const next = reels[currentIndex + 1];
      if (next) {
        onSelectReel(next);
      } else {
        onExitPreview?.();
      }
    };

    const hasAck = !!ackNode || !!ackFlag;
    const ackTexts = getAckTexts(ackLanguage);

    // Attestation and questions are mutually exclusive end actions: with the
    // ack flag set, the acknowledgment replaces any question the generated
    // content may carry; without it, the question (if any) closes the reel.
    const onVideoFinished = () => {
      if (!learnerMode) {
        goToOffset(1);
        return;
      }
      if (hasAck) {
        setAckShown(true);
      } else if (question) {
        setQuizShown(true);
      } else {
        advance();
      }
    };

    // Like the learner player: Submit shows the result and the authored
    // explanation, Continue moves on.
    const onQuizFinished = () => {
      if (!submitted) {
        setSubmitted(true);
        return;
      }
      advance();
    };

    const showAck = learnerMode && ackShown && hasAck;
    const showQuiz = learnerMode && quizShown && !showAck && !!question;

    return (
      <div className={styles.container}>
        <div className={styles.phone}>
          <div className={styles.screen}>
            {currentIndex >= 0 && (
              <div className={styles.positionChip}>
                {microreelsListText.previewPosition(
                  groupTitle,
                  currentIndex + 1,
                  reels.length,
                )}
              </div>
            )}
            {showQuiz && question ? (
              <div className={styles.quizArea}>
                <div className={styles.quizHeader}>
                  <p className={styles.quizTitle}>
                    {microreelsListText.phoneQuizTitle}
                  </p>
                  <p className={styles.quizSubtitle}>
                    {microreelsListText.phoneQuizSubtitle}
                  </p>
                  <span className={styles.quizUnderline} />
                </div>
                <div className={styles.quizCard}>
                  <p className={styles.quizQuestion}>{question.question}</p>
                  <div className={styles.quizOptions}>
                    {question.answers.map((answer, index) => {
                      const revealed = submitted;
                      const optionClass = [
                        styles.quizOption,
                        !revealed && pickedAnswer === index
                          ? styles.quizOptionSelected
                          : '',
                        revealed && answer.isCorrect
                          ? styles.quizOptionCorrect
                          : '',
                        revealed && pickedAnswer === index && !answer.isCorrect
                          ? styles.quizOptionWrong
                          : '',
                      ].join(' ');
                      return (
                        <button
                          key={index}
                          className={optionClass}
                          aria-pressed={pickedAnswer === index}
                          onClick={() => {
                            if (!submitted) {
                              setPickedAnswer(index);
                            }
                          }}>
                          {answer.value}
                        </button>
                      );
                    })}
                  </div>
                  {submitted && pickedAnswer !== null && (
                    <div
                      className={styles.quizResult}
                      role="status"
                      aria-live="polite">
                      <p className={styles.quizResultTitle}>
                        {question.answers[pickedAnswer]?.isCorrect
                          ? microreelsListText.phoneQuizCorrect
                          : microreelsListText.phoneQuizIncorrect}
                      </p>
                      {question.feedback?.trim() && (
                        <>
                          <p className={styles.quizFeedbackTitle}>
                            {microreelsListText.phoneQuizExplanation}
                          </p>
                          <p className={styles.quizFeedback}>
                            {question.feedback.trim()}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <button
                  className={styles.quizFinish}
                  disabled={pickedAnswer === null}
                  onClick={onQuizFinished}>
                  {submitted
                    ? microreelsListText.phoneQuizContinue
                    : microreelsListText.phoneQuizFinish}
                </button>
              </div>
            ) : showAck ? (
              <div className={styles.quizArea}>
                <div className={styles.quizHeader}>
                  <p className={styles.quizTitle}>{ackTexts.heading}</p>
                  <p className={styles.quizSubtitle}>{ackTexts.subtitle}</p>
                  <span className={styles.quizUnderline} />
                </div>
                <div className={styles.quizCard}>
                  <p className={styles.quizQuestion}>
                    {ackNode?.title || ackTexts.question}
                  </p>
                </div>
                {ackNode ? (
                  <button className={styles.quizFinish} onClick={advance}>
                    {ackNode.buttonTitle || microreelsListText.phoneAckButton}
                  </button>
                ) : (
                  <div className={styles.ackButtons}>
                    <button className={styles.ackNoButton} onClick={advance}>
                      {ackTexts.no}
                    </button>
                    <button className={styles.quizFinish} onClick={advance}>
                      {ackTexts.yes}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.playerArea}>
                {isLoading && (
                  <div className={styles.placeholder}>
                    <LoadingSpinner />
                  </div>
                )}
                {!isLoading && videoSources.length === 0 && (
                  <div className={styles.placeholder}>
                    <Icon
                      name="microreel.svg"
                      className={styles.placeholderIcon}
                    />
                    <p>
                      {presentation
                        ? microreelsListText.previewNoVideo
                        : microreelsListText.previewEmpty}
                    </p>
                  </div>
                )}
                {!isLoading && videoSources.length > 0 && (
                  <>
                    <MicroreelVideoSurface
                      sources={videoSources}
                      fillFrame={fillFrame}
                      onFinished={onVideoFinished}
                      onProgress={onProgress}
                      initialMuted={mutedPreferenceRef.current}
                      onMutedChange={muted => {
                        mutedPreferenceRef.current = muted;
                      }}
                    />
                    <button
                      type="button"
                      className={styles.fitButton}
                      onClick={() => setFillFrame(!fillFrame)}
                      title={
                        fillFrame
                          ? microreelsListText.fitToFrame
                          : microreelsListText.fillFrame
                      }
                      aria-label={
                        fillFrame
                          ? microreelsListText.fitToFrame
                          : microreelsListText.fillFrame
                      }>
                      {fillFrame ? (
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden>
                          <path
                            d="M10 14l-6 6m0 0h5m-5 0v-5M14 10l6-6m0 0h-5m5 0v5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden>
                          <path
                            d="M4 20l6-6m-6 6h5m-5 0v-5M20 4l-6 6m6-6h-5m5 0v5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.controls}>
          {learnerMode ? (
            <button
              type="button"
              className={styles.exitButton}
              onClick={onExitPreview}>
              {microreelsListText.exitPreview}
            </button>
          ) : (
            <>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => goToOffset(-1)}
                disabled={currentIndex <= 0}
                aria-label={microreelsListText.previousReel}>
                <span className={`icon-chevron-right ${styles.flipped}`} />
              </button>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => goToOffset(1)}
                disabled={currentIndex < 0 || currentIndex >= reels.length - 1}
                aria-label={microreelsListText.nextReel}>
                <span className="icon-chevron-right" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  },
);

export default MicroreelPhonePreview;
